import React, { useState, useEffect } from 'react';
import moment from 'moment';

import Calendar from '@/Components/Inputs/DatePicker/Calendar.js';

import Button from "@/Components/Inputs/Button";

import { CalendarIcon } from "@/Icons";

import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    FloatingFocusManager
} from '@floating-ui/react';


import InputMask from 'react-input-mask';
import DatePickerTextField from './DatePickerTextField';

function TimeInput(props) {
  let mask2 = 'mM/dD/YYYY';
  

  let formatChars2 = {
    'Y': '[0-9]',
    'd': '[0-3]',
    'D': '[0-9]',
    'm': '[0-1]',
    'M': '[0-9]'
  };

  let beforeMaskedValueChange = (newState, oldState, userInput) => {
    let { value } = newState;

    let dateParts = value.split('/');
    
    let monthPart = dateParts[0];
    let dayPart = dateParts[1];
    
    // Conditional mask for the 2nd digit of month based on the first digit
    if(monthPart.startsWith('1')) {
        formatChars2['M'] = '[0-2]'; // To block 15, 16, etc.
    }
        
    else {
        
        formatChars2['M'] = '[1-9]'; // To allow 05, 06  etc - but blocking 00.
    }
        


    // Conditional mask for the 2nd digit of day based on the first digit
    if(dayPart.startsWith('3'))
      formatChars2['D'] = '[0-1]'; // To block 39, 32, etc.
    else if(dayPart.startsWith('0'))
      formatChars2['D'] = '[1-9]'; // To block 00.
    else
      formatChars2['D'] = '[0-9]'; // To allow 05, 15, 25  etc.

    

    return {value, selection: newState.selection};
  }

  return (
   <InputMask
      mask={mask2}
      value={props.value}
      onChange={props.onChange}
      formatChars={formatChars2}
      onKeyDown={props.onKeyDown}
      beforeMaskedValueChange={beforeMaskedValueChange}>
        {(inputProps) => <DatePickerTextField {...inputProps} />}
    </InputMask>
  );
}

const DatePickerManual = ({
    label,
    value,
    singleDate,
    onSetSelectedDate,
    onApply,
    selectedOption,
    className,
    withFooterActions,
    disablePastDates,
    disableFutureDates = true,
    initialText,
    disabled,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [offset(10), flip(), shift()],
        whileElementsMounted: autoUpdate,
        placement: 'bottom-start'
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);

    // Merge all the interactions into prop getters
    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        dismiss,
        role
    ]);

    const today = new Date();

    const parseDate = date => {
        return moment(new Date(date)).format('MM/DD/YYYY');
    };

    const [date, setDate] = useState(today);
    const [selectedDate, setSelectedDate] = useState(parseDate(date));


    const [labelText, setLabelText] = useState(selectedDate ? parseDate(selectedDate) : initialText);
    const [manualDate, setManualDate] = useState(parseDate(selectedDate));


    
    useEffect(() => {
        if (value) {
            setSelectedDate(value);
            return;
        }

        setLabelText(initialText || '');
    }, [value, initialText]);

    const handleSetDate = e => {
        setDate(e);
    };

    const handleSetSelectedDate = e => {
        if (withFooterActions) {
            setSelectedDate(e.singleDate);
            setManualDate(parseDate(e.singleDate || new Date()));
        } else {
            onSetSelectedDate(e);
            setLabelText(parseDate(e.singleDate));
            setManualDate(e.singleDate);

            setTimeout(() => {
                setIsOpen(false);
            }, 100);
        }
    };

    const handleApply = () => {
        onSetSelectedDate({ singleDate: selectedDate || new Date() });
        setLabelText(parseDate(selectedDate || new Date()));

        setTimeout(() => {
            setIsOpen(false);
        }, 100);
    };

    const renderFooterActions = () => {
        return (
            <div className="date-picker-calendar-footer">
                <Button className="ml-auto uppercase font-semibold" onClick={handleApply}>Apply</Button>
            </div>
        );
    };

    const handleInputKeyDown = (event) => {
        if (event && event.key === 'Enter') {
            onSetSelectedDate({ singleDate: manualDate || new Date() });
            setLabelText(parseDate(manualDate || new Date()));

            setTimeout(() => {
                setIsOpen(false);
            }, 100);
        }
    }

    const handleInput = ({ target: { value } }) => {

        let dateParts = value.split('/');
    
        let monthPart = dateParts[0];

        
        let dayPart = dateParts[1];
        
        setManualDate(value);

        if(value.indexOf('_') === -1 && monthPart < 13 && dayPart < 32) {
            setSelectedDate(value)
            setDate(new Date(value))
        }
    }

    const handleButtonOnClick = () => {
        if (disabled) {
            return;
        }

        setIsOpen(!isOpen);
    };

    return (
        <div className={`date-time ${className ? className : ''}`}>
            {label &&
                <div className={`date-picker-label`}>
                    {label}
                </div>
            }
            <button onClick={handleButtonOnClick} className={`date-time-button`} disabled={disabled} ref={refs.setReference} {...getReferenceProps()}>
                <div className="date-time-content">
                    <div className="flex items-center justify-center gap-[4px]">
                        <CalendarIcon />
                        <span className="date-time-label">
                            {initialText &&
                                <span className={labelText === initialText ? 'date-time-label-initial-text' : ''}>{labelText ? labelText : initialText}</span>
                            }
                            {!initialText &&
                                <span>{labelText}</span>
                            }
                        </span>
                    </div>
                </div>
            </button>
            {isOpen && (
                <FloatingFocusManager context={context} modal={false}>
                    <div
                        className={`date-picker-menu`}
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                    >
                        <div className="date-picker-calendar">
                            <div className="date-picker-calendar-body flex flex-col gap-2">

                                <TimeInput
                                    value={manualDate}
                                    onChange={handleInput}
                                    onKeyDown={handleInputKeyDown}/>

                                <Calendar
                                    singleDate={singleDate}
                                    date={date}
                                    today={today}
                                    selectedDate={selectedDate}
                                    onSetSelectedDate={handleSetSelectedDate}
                                    setDate={handleSetDate}
                                    disablePastDates={disablePastDates}
                                    disableFutureDates={disableFutureDates}
                                />
                            </div>
                            {withFooterActions && singleDate && renderFooterActions()}
                        </div>
                    </div>
                </FloatingFocusManager>
            )}
        </div>
    );
};

export default DatePickerManual;
